import api from "./api";
import { globalCache } from "../hooks/useCache";
import { withRetry, logError, getErrorMessage, toastError } from "../utils/errorHandler";

export const productService = {
  // Get all products with filters and pagination
  getProducts: async (params = {}) => {
    try {
      // Create cache key from params
      const cacheKey = `products:${JSON.stringify(params)}`;

      // Check cache first
      const cachedData = globalCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const response = await withRetry(
        () => api.get("/products", { params }),
        3,
        "getProducts"
      );

      // Cache the result for 5 minutes
      globalCache.set(cacheKey, response.data, 300000);

      return response.data;
    } catch (error) {
      logError(error, "getProducts", { params });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Get single product by ID
  getProduct: async (id) => {
    try {
      const cacheKey = `product:${id}`;

      // Check cache first
      const cachedData = globalCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const response = await withRetry(
        () => api.get(`/products/${id}`),
        3,
        "getProduct"
      );

      // Cache individual product for 10 minutes
      globalCache.set(cacheKey, response.data, 600000);

      return response.data;
    } catch (error) {
      logError(error, "getProduct", { productId: id });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Get single product by Slug
  getProductBySlug: async (slug) => {
    try {
      const cacheKey = `product:slug:${slug}`;

      // Check cache first
      const cachedData = globalCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const response = await withRetry(
        () => api.get(`/products/slug/${slug}`),
        3,
        "getProductBySlug"
      );

      // Cache individual product for 10 minutes
      globalCache.set(cacheKey, response.data, 600000);

      return response.data;
    } catch (error) {
      logError(error, "getProductBySlug", { slug });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Search products
  searchProducts: async (query, filters = {}) => {
    try {
      const params = { search: query, ...filters };
      const response = await withRetry(
        () => api.get("/products", { params }),
        3,
        "searchProducts"
      );
      return response.data;
    } catch (error) {
      logError(error, "searchProducts", { query, filters });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Get products by category
  getProductsByCategory: async (category, filters = {}) => {
    try {
      const params = { category, ...filters };
      const response = await withRetry(
        () => api.get("/products", { params }),
        3,
        "getProductsByCategory"
      );
      return response.data;
    } catch (error) {
      logError(error, "getProductsByCategory", { category, filters });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Get related products
  getRelatedProducts: async (productId) => {
    try {
      const cacheKey = `products:related:${productId}`;
      const cachedData = globalCache.get(cacheKey);
      if (cachedData) return cachedData;

      const response = await withRetry(
        () => api.get(`/products/${productId}/related`),
        3,
        "getRelatedProducts"
      );
      
      globalCache.set(cacheKey, response.data, 3600000); // 1 hour
      return response.data;
    } catch (error) {
      logError(error, "getRelatedProducts", { productId });
      return { success: false, data: { products: [] } };
    }
  },

  // Get featured products
  getFeaturedProducts: async () => {
    try {
      const cacheKey = "products:featured";

      // Check cache first
      const cachedData = globalCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const response = await withRetry(
        () => api.get("/products", { params: { featured: true } }),
        3,
        "getFeaturedProducts"
      );

      // Cache featured products for 15 minutes
      globalCache.set(cacheKey, response.data, 900000);

      return response.data;
    } catch (error) {
      logError(error, "getFeaturedProducts");
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Get product categories
  getCategories: async () => {
    try {
      const response = await withRetry(
        () => api.get("/products/categories"),
        3,
        "getCategories"
      );
      return response.data;
    } catch (error) {
      logError(error, "getCategories");
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  getShippingCountries: async () => {
    try {
      const response = await withRetry(
        () => api.get("/shipping/countries"),
        3,
        "getShippingCountries",
      );
      return response.data;
    } catch (error) {
      logError(error, "getShippingCountries");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  getShippingCarriers: async () => {
    try {
      const response = await withRetry(
        () => api.get("/shipping/carriers"),
        3,
        "getShippingCarriers",
      );
      return response.data;
    } catch (error) {
      logError(error, "getShippingCarriers");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  getProductShippingRates: async (productId) => {
    try {
      const response = await withRetry(
        () => api.get(`/admin/products/${productId}/shipping-rates`),
        2,
        "getProductShippingRates",
      );
      return response.data;
    } catch (error) {
      logError(error, "getProductShippingRates", { productId });
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  saveProductShippingRates: async (productId, payload) => {
    try {
      const response = await withRetry(
        () => api.post(`/admin/products/${productId}/shipping-rates`, payload),
        2,
        "saveProductShippingRates",
      );
      return response.data;
    } catch (error) {
      logError(error, "saveProductShippingRates", { productId });
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  deleteProductShippingRate: async (productId, rateId) => {
    try {
      const response = await withRetry(
        () => api.delete(`/admin/products/${productId}/shipping-rates/${rateId}`),
        2,
        "deleteProductShippingRate",
      );
      return response.data;
    } catch (error) {
      logError(error, "deleteProductShippingRate", { productId, rateId });
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Admin functions
  createProduct: async (productData) => {
    try {
      const response = await withRetry(
        () => api.post("/products", productData),
        2,
        "createProduct"
      );

      globalCache.clear("products:");

      return response.data;
    } catch (error) {
      logError(error, "createProduct", { productName: productData.name });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  updateProduct: async (id, productData) => {
    try {
      const response = await withRetry(
        () => api.put(`/products/${id}`, productData),
        2,
        "updateProduct"
      );

      globalCache.clear("products:");
      globalCache.delete(`product:${id}`);

      return response.data;
    } catch (error) {
      logError(error, "updateProduct", {
        productId: id,
        productName: productData.name,
      });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  deleteProduct: async (id) => {
    try {
      const response = await withRetry(
        () => api.delete(`/products/${id}`),
        2,
        "deleteProduct"
      );

      globalCache.clear("products:");
      globalCache.delete(`product:${id}`);

      return response.data;
    } catch (error) {
      logError(error, "deleteProduct", { productId: id });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Create product with images
  createProductWithImages: async (productData, images) => {
    try {
      const formData = new FormData();

      // Add images
      if (images && images.length > 0) {
        images.forEach((image) => {
          formData.append("images", image);
        });
      }

      // Add product data
      Object.keys(productData).forEach((key) => {
        if (key === "measurements") {
          // Handle measurements object
          Object.keys(productData.measurements).forEach((measureKey) => {
            if (productData.measurements[measureKey] !== undefined) {
              formData.append(
                `measurements.${measureKey}`,
                productData.measurements[measureKey]
              );
            }
          });
        } else if (key === "tags") {
          // Handle tags array
          if (Array.isArray(productData.tags)) {
            productData.tags.forEach((tag) => {
              formData.append("tags", tag);
            });
          }
        } else {
          formData.append(key, productData[key]);
        }
      });

      const response = await withRetry(
        () =>
          api.post("/products/with-images", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }),
        2,
        "createProductWithImages"
      );

      // Clear relevant cache entries
      globalCache.clear("products:");

      return response.data;
    } catch (error) {
      logError(error, "createProductWithImages", {
        productName: productData.name,
        imageCount: images?.length || 0,
      });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Update product with images
  updateProductWithImages: async (
    id,
    productData,
    images,
    replaceImages = false
  ) => {
    try {
      const formData = new FormData();

      // Add images if provided
      if (images && images.length > 0) {
        images.forEach((image) => {
          formData.append("images", image);
        });
      }

      // Add replace images flag
      formData.append("replaceImages", replaceImages.toString());

      // Add product data
      Object.keys(productData).forEach((key) => {
        if (key === "measurements") {
          // Handle measurements object
          Object.keys(productData.measurements).forEach((measureKey) => {
            if (productData.measurements[measureKey] !== undefined) {
              formData.append(
                `measurements.${measureKey}`,
                productData.measurements[measureKey]
              );
            }
          });
        } else if (key === "tags") {
          // Handle tags array
          if (Array.isArray(productData.tags)) {
            productData.tags.forEach((tag) => {
              formData.append("tags", tag);
            });
          }
        } else {
          formData.append(key, productData[key]);
        }
      });

      const endpoint =
        images && images.length > 0
          ? `/products/${id}/with-images`
          : `/products/${id}`;

      const requestData = images && images.length > 0 ? formData : productData;
      const headers =
        images && images.length > 0
          ? { "Content-Type": "multipart/form-data" }
          : { "Content-Type": "application/json" };

      const response = await withRetry(
        () => api.put(endpoint, requestData, { headers }),
        2,
        "updateProductWithImages"
      );

      // Clear relevant cache entries
      globalCache.clear("products:");
      globalCache.delete(`product:${id}`);

      return response.data;
    } catch (error) {
      logError(error, "updateProductWithImages", {
        productId: id,
        productName: productData.name,
        imageCount: images?.length || 0,
      });
      toastError(error, "product");
      const message = getErrorMessage(error, "product");
      throw new Error(message);
    }
  },

  // Upload single product image
  uploadSingleImage: async (image, oldImage) => {
    try {
      const formData = new FormData();
      formData.append("image", image);
      if (oldImage) {
        formData.append("oldImage", oldImage);
      }

      const response = await withRetry(
        () =>
          api.post("/upload/product/single", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }),
        2,
        "uploadSingleImage"
      );
      return response.data;
    } catch (error) {
      logError(error, "uploadSingleImage", { fileName: image?.name, oldImage });
      toastError(error, "product");
      const message = getErrorMessage(error, "upload");
      throw new Error(message);
    }
  },

  // Upload multiple product images
  uploadMultipleImages: async (images) => {
    try {
      const formData = new FormData();
      images.forEach((image) => {
        formData.append("images", image);
      });

      const response = await withRetry(
        () =>
          api.post("/upload/product/multiple", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }),
        2,
        "uploadMultipleImages"
      );
      return response.data;
    } catch (error) {
      logError(error, "uploadMultipleImages", { imageCount: images.length });
      toastError(error, "product");
      const message = getErrorMessage(error, "upload");
      throw new Error(message);
    }
  },

  // Delete product image
  deleteProductImage: async (filename) => {
    try {
      const response = await withRetry(
        () => api.delete(`/upload/product/${filename}`),
        2,
        "deleteProductImage"
      );
      return response.data;
    } catch (error) {
      logError(error, "deleteProductImage", { filename });
      toastError(error, "product");
      const message = getErrorMessage(error, "upload");
      throw new Error(message);
    }
  },

  // Get image secondary
  getImageInfo: async (filename) => {
    try {
      const response = await withRetry(
        () => api.get(`/upload/product/${filename}/secondary`),
        2,
        "getImageInfo"
      );
      return response.data;
    } catch (error) {
      logError(error, "getImageInfo", { filename });
      toastError(error, "product");
      const message = getErrorMessage(error, "upload");
      throw new Error(message);
    }
  },
};

export default productService;
